import { useEffect, useRef, useState } from 'react';
import api from '../services/api';
import useLogActivityAccess from '../hooks/useLogActivityAccess';

const TEMPOS = ['Present Simple', 'Past Simple', 'Past Participle', 'Present Continuous', 'Future Simple'];

const S = {
  page: { minHeight: 'calc(100vh - 66px)', background: 'linear-gradient(160deg,#0a1628 0%,#1a2a4a 100%)', padding: '32px 20px', fontFamily: 'Poppins,sans-serif' },
  inner: { maxWidth: 960, margin: '0 auto' },
  title: { color: '#fff', fontSize: 'clamp(1.5rem,4vw,2.2rem)', fontWeight: 800, textAlign: 'center', marginBottom: 6 },
  subtitle: { color: 'rgba(255,255,255,0.55)', textAlign: 'center', fontSize: '0.95rem', marginBottom: 28 },
  tabs: { display: 'flex', gap: 10, justifyContent: 'center', marginBottom: 28, flexWrap: 'wrap' },
  tab: (active) => ({ padding: '9px 26px', borderRadius: 30, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '0.92rem', fontFamily: 'Poppins,sans-serif', background: active ? '#1e90ff' : 'rgba(255,255,255,0.1)', color: active ? '#fff' : 'rgba(255,255,255,0.65)', transition: 'all 0.2s' }),
  search: { display: 'block', width: '100%', maxWidth: 380, margin: '0 auto 28px', padding: '11px 18px', borderRadius: 30, border: 'none', background: 'rgba(255,255,255,0.1)', color: '#fff', fontSize: '0.95rem', outline: 'none', fontFamily: 'Poppins,sans-serif' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 18 },
  card: { background: 'rgba(255,255,255,0.06)', borderRadius: 16, border: '1px solid rgba(255,255,255,0.1)', overflow: 'hidden', cursor: 'pointer', transition: 'transform 0.2s,box-shadow 0.2s' },
  cardHead: (tipo) => ({ background: tipo === 'regular' ? 'linear-gradient(135deg,#2ecc71,#27ae60)' : 'linear-gradient(135deg,#e74c3c,#c0392b)', padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }),
  cardVerbo: { color: '#fff', fontWeight: 800, fontSize: '1.3rem', margin: 0 },
  cardTrad: { color: 'rgba(255,255,255,0.75)', fontSize: '0.82rem', margin: 0, marginTop: 2 },
  badge: (tipo) => ({ background: 'rgba(255,255,255,0.25)', color: '#fff', borderRadius: 12, padding: '3px 10px', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.5px' }),
  cardBody: { padding: '14px 18px' },
  conjRow: { display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' },
  tempoBadge: { background: 'rgba(30,144,255,0.2)', color: '#7ecfff', borderRadius: 8, padding: '2px 9px', fontSize: '0.74rem', fontWeight: 600, whiteSpace: 'nowrap' },
  conjText: { color: '#fff', fontWeight: 700, fontSize: '0.95rem' },
  fraseText: { color: 'rgba(255,255,255,0.55)', fontSize: '0.82rem', fontStyle: 'italic', marginLeft: 0 },
  audioBtn: { background: 'rgba(30,144,255,0.3)', border: 'none', borderRadius: 8, padding: '4px 10px', cursor: 'pointer', color: '#7ecfff', fontSize: '0.82rem' },
  empty: { textAlign: 'center', color: 'rgba(255,255,255,0.4)', marginTop: 60, fontSize: '1.1rem' },
};

function playTTS(text) {
  fetch('/synt/text-to-speech', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text, languageCode: 'en-US', voiceName: 'en-US-Neural2-J', pitch: 0 })
  })
    .then(r => r.blob())
    .then(blob => { const url = URL.createObjectURL(blob); new Audio(url).play(); })
    .catch(() => {});
}

async function traduzir(texto) {
  const r = await fetch('/traduzir/traduzir', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ texto, idiomaOrigem: 'en', idiomaDestino: 'pt' }),
  });
  const data = await r.json();
  return data.textoTraduzido || '';
}

function ConjugacaoRow({ c }) {
  const [trad, setTrad] = useState(null);
  const [loading, setLoading] = useState(false);

  async function toggleTrad() {
    if (trad !== null) { setTrad(null); return; }
    setLoading(true);
    try { setTrad(await traduzir(c.frase_exemplo)); } catch { setTrad('Erro ao traduzir.'); }
    setLoading(false);
  }

  return (
    <div style={{ marginBottom: 14 }}>
      <div style={S.conjRow}>
        <span style={S.tempoBadge}>{c.tempo}</span>
        <span style={S.conjText}>{c.conjugacao}</span>
        <button style={S.audioBtn} onClick={() => playTTS(c.conjugacao)}>🔊</button>
      </div>
      {c.frase_exemplo && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 4, flexWrap: 'wrap' }}>
            <span style={S.fraseText}>"{c.frase_exemplo}"</span>
            <button style={S.audioBtn} onClick={() => playTTS(c.frase_exemplo)}>🔊</button>
            <button
              style={{ ...S.audioBtn, background: trad !== null ? 'rgba(46,204,113,0.35)' : 'rgba(255,255,255,0.12)', color: trad !== null ? '#7effc0' : 'rgba(255,255,255,0.6)', fontSize: '0.75rem', padding: '3px 9px' }}
              onClick={toggleTrad}
              disabled={loading}
            >
              {loading ? '...' : trad !== null ? 'EN' : 'PT'}
            </button>
          </div>
          {trad !== null && (
            <p style={{ ...S.fraseText, color: '#7effc0', marginTop: 4, paddingLeft: 4 }}>
              "{trad}"
            </p>
          )}
        </>
      )}
    </div>
  );
}

function VerbCard({ verbo }) {
  const [open, setOpen] = useState(false);

  return (
    <div
      style={S.card}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.3)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
    >
      <div style={S.cardHead(verbo.tipo)} onClick={() => setOpen(o => !o)}>
        <div>
          <p style={S.cardVerbo}>{verbo.verbo}</p>
          <p style={S.cardTrad}>{verbo.traducao}</p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
          <span style={S.badge(verbo.tipo)}>{verbo.tipo === 'regular' ? 'Regular' : 'Irregular'}</span>
          <button style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 8, padding: '3px 10px', color: '#fff', cursor: 'pointer', fontSize: '0.8rem' }}
            onClick={e => { e.stopPropagation(); playTTS(verbo.verbo); }}>
            🔊 Palavra
          </button>
        </div>
      </div>

      {open && (
        <div style={S.cardBody}>
          {verbo.conjugacoes.length === 0 && (
            <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.85rem', margin: 0 }}>Sem conjugações cadastradas.</p>
          )}
          {verbo.conjugacoes.map(c => (
            <ConjugacaoRow key={c.id} c={c} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function Verbos() {
  useLogActivityAccess('verbos');
  const [verbos, setVerbos] = useState([]);
  const [tipo, setTipo] = useState('all');
  const [busca, setBusca] = useState('');
  const [loading, setLoading] = useState(true);
  const cacheRef = useRef({});

  useEffect(() => {
    const key = tipo === 'all' ? '' : tipo;
    if (cacheRef.current[key]) { setVerbos(cacheRef.current[key]); setLoading(false); return; }
    setLoading(true);
    const url = tipo === 'all' ? '/verbos' : `/verbos?tipo=${tipo}`;
    api.get(url)
      .then(r => { cacheRef.current[key] = r.data; setVerbos(r.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [tipo]);

  const filtrados = verbos.filter(v =>
    v.verbo.toLowerCase().includes(busca.toLowerCase()) ||
    v.traducao.toLowerCase().includes(busca.toLowerCase())
  );

  return (
    <div style={S.page}>
      <div style={S.inner}>
        <h1 style={S.title}>📚 Verbos em Inglês</h1>
        <p style={S.subtitle}>Explore verbos regulares e irregulares com exemplos de cada tempo verbal</p>

        <div style={S.tabs}>
          {[['all','Todos'],['regular','Regulares'],['irregular','Irregulares']].map(([k,l]) => (
            <button key={k} style={S.tab(tipo===k)} onClick={() => { setTipo(k); setBusca(''); }}>{l}</button>
          ))}
        </div>

        <input
          style={S.search}
          placeholder="🔍 Buscar verbo ou tradução..."
          value={busca}
          onChange={e => setBusca(e.target.value)}
        />

        {loading ? (
          <p style={S.empty}><i className="fas fa-spinner fa-spin" /> Carregando...</p>
        ) : filtrados.length === 0 ? (
          <p style={S.empty}>Nenhum verbo encontrado.</p>
        ) : (
          <div style={S.grid}>
            {filtrados.map(v => <VerbCard key={v.id} verbo={v} />)}
          </div>
        )}
      </div>
    </div>
  );
}
